import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../hooks/useAuthContext';
import {
    Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Button, CircularProgress,
    TableSortLabel, Box, Typography
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getSharedTemplates } from '../../lib/firestore';

const SharedTemplates = () => {
    const { user } = useAuthContext();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                if (!user) return;
                const templatesData = await getSharedTemplates(user.uid);
                setTemplates(templatesData);
            } catch (error) {
                console.error('Error fetching templates:', error);
                setError('Error loading templates');
            } finally {
                setLoading(false);
            }
        };

        fetchTemplates();
    }, [user]);

    const handleRequestSort = (property) => {
        const isAsc = sortField === property && sortOrder === 'asc';
        setSortField(property);
        setSortOrder(isAsc ? 'desc' : 'asc');
    };

    const createSortHandler = (property) => (event) => {
        handleRequestSort(property);
    };

    useEffect(() => {
        const sortedTemplates = [...templates].sort((a, b) => {
            let valueA, valueB;
            if (sortField === 'createdOn') {
                valueA = a.createdOn.toDate().getTime();
                valueB = b.createdOn.toDate().getTime();
            } else {
                valueA = a[sortField];
                valueB = b[sortField];
            }

            if (valueA < valueB) {
                return sortOrder === 'asc' ? -1 : 1;
            } else if (valueA > valueB) {
                return sortOrder === 'asc' ? 1 : -1;
            } else {
                return 0;
            }
        });

        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        setTemplates(sortedTemplates.slice(start, end));
    }, [page, rowsPerPage, sortField, sortOrder]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
                <Typography variant="h6" color="error" gutterBottom>
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'name'}
                                    direction={sortField === 'name' ? sortOrder : 'asc'}
                                    onClick={createSortHandler('name')}
                                >
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Level</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'ownerName'}
                                    direction={sortField === 'ownerName' ? sortOrder : 'asc'}
                                    onClick={createSortHandler('ownerName')}
                                >
                                    Created By
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'createdOn'}
                                    direction={sortField === 'createdOn' ? sortOrder : 'asc'}
                                    onClick={createSortHandler('createdOn')}
                                >
                                    Created
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates.map((template) => (
                            <TableRow key={template.id}>
                                <TableCell>
                                    <Link to={`/templates/${template.id}`} style={{ textDecoration: 'none' }}>
                                        <Typography color="primary">
                                            {template.name}
                                        </Typography>
                                    </Link>
                                </TableCell>
                                <TableCell>{template.class}</TableCell>
                                <TableCell>{template.level}</TableCell>
                                <TableCell>{template.ownerName}</TableCell>
                                <TableCell>{template.createdOn?.toDate().toLocaleDateString()}</TableCell>
                                <TableCell>
                                    <Button
                                        component={Link}
                                        to={`/templates/${template.id}`}
                                        color="primary"
                                        size="small"
                                    >
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={templates.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Box>
    );
};

export default SharedTemplates;
